/** @jsxImportSource @emotion/react */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMessageBar } from 'hooks/useMessageBar';
import { useNavigate } from 'react-router-dom';
import {
  InputBox,
  SearchBox,
  SearchBoxRow,
  SearchButtonWrap,
  SearchCols,
  SearchRows,
} from '../../components/layouts/SearchBox';
import { ComboBox, MultiComboBox } from 'components/selects/ComboBox';
import CustomInputWithSearch from '../../components/inputs/CustomInputWithSearch';
import SearchBoxButtons from '../../components/buttons/SearchBoxButtons';
import { Code } from '../../models/common/CommonCode';
import { findDisplayProject } from '../../apis/setup/SetupProject';
import { SetupProject } from '../../models/setup/SetupProject';
import { CellMaker } from '@grapecity/wijmo.grid.cellmaker';
import { getCommonCodeNames } from '../../apis/admin/CommonCode';
import {
  ControlBtnGroup,
  SubTitleGroup,
  SubTitleLayout,
} from '../../components/layouts/ContentLayout';
import { Button } from '@mui/material';
import { IconButton } from '../../components/buttons/IconSVG';
import CustomGrid from '../../components/grids/CustomGrid';
import { CommonUtil } from '../../utils/CommonUtil';
import {
  checkAvailableSetupEquipments,
  defaultSetupEquipmentSummary,
  findSetupEquipmentSummary,
} from '../../apis/setup/SetupEquipment';
import { useLoading } from '../../components/process/Loading';
import { SetupEquipment, SetupEquipmentCondition } from '../../models/setup/SetupEquipment';
import { downloadExcelTemplatesPost } from '../../apis/common/Excel';
import UserMultiModal from '../../components/modals/common/UserMultiModal';
import useSessionStore from '../../stores/useSessionStore';
import { RoleCode } from '../../models/common/RoleCode';

/**
 * @desc 설비셋업관리 > 프로젝트관리 > 셋업설비 조회 화면
 * @author
 * -------------------------------------------------------------------------------------
 *  Date.       author         Description
 * -------------------------------------------------------------------------------------
 * 2025.10.24   jd            신규 생성
 * 2026.07.01   jshwang12       설비단계자동설정 기능 적용 (공통코드에서 테이블로 변경)
 */
interface PageLocationState {
  elmPrjId?: string;
}
const SetupEquipmentPage = () => {
  const { t } = useTranslation();
  const { openLoading } = useLoading();
  const { openMessageBar } = useMessageBar();
  const userSession = useSessionStore();
  const navigate = useNavigate();
  const gridRef = useRef<any>();
  const [searchParams] = useSearchParams();
  const { state } = useLocation();
  const [locationState, setLocationState] = useState<PageLocationState>(useLocation().state);

  const [flexRef, setFlexRef] = useState<any>();
  const [rowData, setRowData] = useState<SetupEquipment[]>([]);

  const [bildPlntCode, setBildPlntCode] = useState<Code[]>([]);

  const defaultCondition = {
    bildPlntCd: '',
    bildPlntCds: [],
    elmPrjId: '',
    elmPrjNm: '',
    searchDataInsUserId: '',
  };
  const [setupEquipmentCondition, setSetupEquipmentCondition] =
    useState<SetupEquipmentCondition>(defaultCondition);
  const [prjMasterData, setPrjMasterData] = useState<SetupProject[]>([]);
  const [userModalMode, setUserModalMode] = useState<string>('');
  const [wrtUserNm, setWrtUserNm] = useState<string>('');
  const [isOpenUserModal, setOpenUserModal] = useState<boolean>(false);

  const searchUserRoles = useMemo(() => {
    return [RoleCode.FA_USER, RoleCode.FA_USER_COP, RoleCode.FA_MANAGER];
  }, [isOpenUserModal]);

  useEffect(() => {
    const init = async () => {
      await initCodes();
      await setDefaultConditionAndSearch();
    };
    init();
  }, []);

  useEffect(() => {
    if (locationState?.elmPrjId) {
      handleOnChange('elmPrjId', locationState?.elmPrjId);
    }
  }, [locationState?.elmPrjId]);

  useEffect(() => {
    if (state) {
      setLocationState(state);
    }
  }, [state]);

  const initCodes = async () => {
    const bildPlntCode = await getCommonCodeNames('FA_SU_PLNT_CD');
    setBildPlntCode(bildPlntCode);

    findDisplayProject().then((result: SetupProject[]) => {
      if (result !== null) {
        setPrjMasterData(result);
      } else {
        openMessageBar({
          type: 'error',
          content: t(
            'com.msg.요청 정보 조회 중 오류가 발생했습니다.',
            '요청 정보 조회 중 오류가 발생했습니다.'
          ),
        });
      }
    });
  };

  const layoutDefinition = useMemo(() => {
    return [
      {
        binding: 'elmPrjId',
        visible: false,
      },
      {
        binding: 'no',
        header: String(t('com.label.NO', 'NO')),
        align: 'center',
        width: 40,
        isReadOnly: true,
        cellTemplate: (grid) => grid.row._idx + 1,
      },
      {
        binding: 'bildPlntCdNm',
        header: String(t('setup.label.플랜트', '플랜트')),
        isReadOnly: true,
        width: 110,
        align: 'center',
      },
      {
        binding: 'elmPrjNm',
        header: String(t('setup.label.프로젝트명', '프로젝트명')),
        isReadOnly: true,
        width: '*',
        minWidth: 210,
        align: 'left',
        cellTemplate: CellMaker.makeLink({
          text: '<span>${item.elmPrjNm}</span>',
          click: (e, ctx) => {
            moveToPage(ctx.item);
          },
        }),
      },
      {
        binding: 'prdnProcCd',
        visible: false,
      },
      {
        binding: 'eqpCnt',
        header: String(t('setup.label.설비셋업대수(Machine)', '설비셋업대수(Machine)')),
        width: 120,
        align: 'center',
        isReadOnly: true,
        isRequired: false,
      },
      {
        binding: 'regStatCd',
        header: String(t('setup.label.설비셋업상태', '설비셋업상태')),
        width: 120,
        align: 'center',
        isReadOnly: true,
        isRequired: false,
        cssClass: 'WijmoTag',
        cellTemplate: (item) => {
          if (item.item.regStatCd === 'REG_CPLT') {
            return `<span class="yellow">${item.item.regStatCdNm}</span>`;
          } else if (item.item.regStatCd === 'SAVE') {
            return `<span class="green">${item.item.regStatCdNm}</span>`;
          } else {
            return `<span class="blue">${item.item.regStatCdNm}</span>`;
          }
        },
      },
      {
        binding: 'procNms',
        header: String(t('setup.label.공정', '공정')),
        width: 230,
        align: 'left',
        isReadOnly: true,
      },
      {
        binding: 'dataInsUserNm',
        header: String(t('com.label.작성자', '작성자')),
        width: 120,
        align: 'center',
      },
      {
        binding: 'dataInsDtm',
        header: String(t('com.label.작성일', '작성일')),
        width: 140,
        align: 'center',
      },
      {
        header: String(t('setup.label.수정자', '수정자')),
        binding: 'dataUpdUserNm',
        minWidth: 120,
        width: 140,
        align: 'center',
      },
      {
        binding: 'dataUpdDtm',
        header: String(t('setup.label.수정일', '수정일')),
        minWidth: 120,
        width: 140,
        align: 'center',
      },
    ];
  }, [rowData]);

  const onInitialized = (grid) => {
    setFlexRef(grid);

    grid.hostElement.addEventListener('click', (e) => {
      if (grid.rows.length == 0) return;

      const ht = grid.hitTest(e);
      if (ht.row < 0 || ht.col < 0) return;
      // grid.startEditing(true);

      if (ht.panel.cellType != 1) return;

      const binding = grid.columns[ht.col].binding;
      const item = grid.rows[ht.row].dataItem;
    });
  };

  const setDefaultConditionAndSearch = async () => {
    defaultSetupEquipmentSummary().then((result) => {
      const condition =
        searchParams?.get('elmPrjId') || locationState?.elmPrjId || userSession?.elmPrjId
          ? ({
              ...setupEquipmentCondition,
              elmPrjId:
                searchParams?.get('elmPrjId') || locationState?.elmPrjId || userSession?.elmPrjId,
              bildPlntCds: [],
            } as SetupEquipmentCondition)
          : result
          ? ({
              ...setupEquipmentCondition,
              elmPrjId: result?.elmPrjId,
              bildPlntCds: result?.bildPlntCd ? [result?.bildPlntCd] : [],
            } as SetupEquipmentCondition)
          : defaultCondition;
      setSetupEquipmentCondition(condition);

      openLoading(true);
      findSetupEquipmentSummary(condition)
        .then((result: SetupEquipment[]) => {
          if (result !== null) {
            setRowData(result || []);
          } else {
            openMessageBar({
              type: 'error',
              content: t(
                'com.msg.요청 정보 조회 중 오류가 발생했습니다.',
                '요청 정보 조회 중 오류가 발생했습니다.'
              ),
            });
          }
        })
        .finally(() => openLoading(false));
    });
  };

  const btnSearch = async () => {
    openLoading(true);
    await findSetupEquipmentSummary(setupEquipmentCondition)
      .then((result: SetupEquipment[]) => {
        if (result !== null) {
          setRowData(result || []);
        } else {
          openMessageBar({
            type: 'error',
            content: t(
              'com.msg.요청 정보 조회 중 오류가 발생했습니다.',
              '요청 정보 조회 중 오류가 발생했습니다.'
            ),
          });
        }
      })
      .finally(() => openLoading(false));
  };

  const btnExcelTemplate = async (templateType) => {
    openLoading(true);
    downloadExcelTemplatesPost(templateType, setupEquipmentCondition?.elmPrjId || '', []).finally(
      () => openLoading(false)
    );
  };

  const callbackUserData = (callbackData) => {
    if (userModalMode === 'searchDataInsUserId') {
      setSetupEquipmentCondition({
        ...setupEquipmentCondition,
        searchDataInsUserId: callbackData.userId,
        dataInsUserNm: callbackData.empNm,
      });
      setWrtUserNm(callbackData.empNm);
    }
  };

  const moveToPage = (item: any) => {
    if (item?.elmPrjId) {
      checkAvailableSetupEquipments(item?.elmPrjId).then((res) => {
        if (res.successOrNot === 'Y' && res.data === true) {
          const searchPageCondition = {
            mode: 'READ',
            upprMnuUrl: location.pathname,
            mnuNm: t('setup.label.셋업설비등록', '셋업설비등록'),
            elmPrjId: item.elmPrjId,
          };
          navigate(`/setup/equipment-detail?elmPrjId=${item.elmPrjId}`, {
            state: { ...searchPageCondition },
          });
        } else {
          openMessageBar({
            type: 'warning',
            content:
              res.data && typeof res.data === 'string'
                ? res.data
                : t(
                    'setup.label.프로젝트 단계 설정이 모두 완료되지 않았습니다.',
                    '프로젝트 단계 설정이 모두 완료되지 않았습니다.'
                  ),
          });
        }
      });
    }
  };

  const optionsPrjMaster = useMemo(() => {
    return (prjMasterData || [])
      .filter((o) =>
        (setupEquipmentCondition?.bildPlntCds || []).length > 0
          ? setupEquipmentCondition.bildPlntCds.includes(o.bildPlntCd)
          : true
      )
      .reduce(
        (acc, cur) => [
          ...acc,
          {
            cmnCd: cur.elmPrjId,
            cmnCdNm: cur.elmPrjNm,
          } as Code,
        ],
        [] as Code[]
      );
  }, [prjMasterData, setupEquipmentCondition?.bildPlntCds]);

  const handleOnChange = (name, value) => {
    setSetupEquipmentCondition((prev) => ({ ...prev, [name]: value }));
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter') {
      btnSearch();
    }
  };

  const btnExcelExport = () => {
    CommonUtil.exportWijmoExcelGrid(
      flexRef,
      t('setup.label.셋업설비 조회', '셋업설비 조회'),
      t('setup.label.설비셋업 진행 목록', '설비셋업 진행 목록')
    );
  };

  return (
    <>
      <SearchBox>
        <SearchBoxRow>
          <InputBox>
            <SearchRows>
              <SearchCols>
                <label>{String(t('setup.label.플랜트', '플랜트'))}</label>
                <MultiComboBox
                  id="bildPlntCd"
                  options={bildPlntCode || []}
                  placeholder={String(t('com.label.전체', '전체'))}
                  defaultValue={setupEquipmentCondition?.bildPlntCds || []}
                  onChange={(value) => {
                    handleOnChange('bildPlntCds', value);
                    if (
                      !(prjMasterData || []).filter(
                        (o) =>
                          setupEquipmentCondition?.elmPrjId === o.elmPrjId &&
                          ((value || []).length < 1 || (value || []).includes(o.bildPlntCd || ''))
                      ).length
                    ) {
                      handleOnChange('elmPrjId', '');
                    }
                  }}
                />
              </SearchCols>
              <SearchCols>
                <label>{String(t('setup.label.프로젝트명', '프로젝트명'))}</label>
                <ComboBox
                  placeholder={String(t('com.label.전체', '전체'))}
                  options={optionsPrjMaster}
                  defaultValue={setupEquipmentCondition?.elmPrjId}
                  onChange={(value) => {
                    handleOnChange('elmPrjId', value);
                  }}
                />
              </SearchCols>
              <SearchCols>
                <label>{t('setup.label.작성자', '작성자')}</label>
                <CustomInputWithSearch
                  name="userId"
                  className="find"
                  value={setupEquipmentCondition.dataInsUserNm || ''}
                  defaultValue={setupEquipmentCondition.dataInsUserNm || ''}
                  placeholder={String(
                    t('com.label.작성자를 선택해 주세요.', '작성자를 선택해 주세요.')
                  )}
                  onChange={(e) => {
                    setWrtUserNm(e.target.value);
                  }}
                  onSearchClick={(e) => {
                    setWrtUserNm(setupEquipmentCondition.dataInsUserNm || '');
                    setUserModalMode('searchDataInsUserId');
                    setOpenUserModal(true);
                  }}
                  onKeyDown={onKeyDown}
                />
              </SearchCols>
            </SearchRows>
          </InputBox>
          <SearchButtonWrap>
            <SearchBoxButtons
              defaultCondition={defaultCondition}
              setReset={setSetupEquipmentCondition}
              onSearch={() => btnSearch()}
            />
          </SearchButtonWrap>
        </SearchBoxRow>
      </SearchBox>
      <SubTitleLayout>
        <SubTitleGroup>
          <h3>{t('setup.label.셋업설비 목록', '셋업설비 목록')}</h3>
          <span className="total">
            {t('com.label.총', '총')}
            <span>{(rowData || []).length.toLocaleString()}</span>
            {t('com.label.건', '건')}
          </span>
        </SubTitleGroup>
        <ControlBtnGroup>
          <Button
            css={IconButton.button}
            className="Exceldown"
            onClick={() => btnExcelTemplate('TPL_SETUP_EQP_LIST')}
            disableRipple
          >
            {t('com.button.템플릿 다운로드', '템플릿 다운로드')}
          </Button>
          <Button
            css={IconButton.button}
            className="Exceldown"
            onClick={btnExcelExport}
            disableRipple
          >
            {t('com.button.다운로드', '화면 다운로드')}
          </Button>
          <Button css={IconButton.button} className="refresh" onClick={btnSearch} disableRipple>
            {t('com.button.새로고침', '새로고침')}
          </Button>
        </ControlBtnGroup>
      </SubTitleLayout>
      <CustomGrid
        layoutDefinition={layoutDefinition}
        rowData={rowData}
        isSelector={false}
        isReadOnly={true}
        initialized={onInitialized}
        height={510}
        allowPinning={false}
      />
      {isOpenUserModal && (
        <UserMultiModal
          open={isOpenUserModal}
          close={() => setOpenUserModal(false)}
          title={t('com.label.담당자 조회', '담당자 조회')}
          mode={userModalMode}
          defaultUserId={setupEquipmentCondition?.[userModalMode]}
          condition={{ roles: searchUserRoles }}
          isOnlyUsed={true}
          singleSelect={true}
          onCallback={(callbackData) => {
            callbackUserData(callbackData);
          }}
        />
      )}
    </>
  );
};

export default SetupEquipmentPage;
